import React from 'react'
import { useIsomorphicLayoutEffect } from 'react-use'
import url from 'url'

import { removeSessionCookies } from 'src/lib/utils'
import Accordion, { H2 } from 'src/components/generics/accordion'
import GlobalLayout from 'src/layouts/global-page'
import { Link, useRouter } from 'src/lib/router'

const accordionItems = [
  {
    key: 'lms',
    term: 'Trying to get to Yellowdig from a course? Coming from Canvas, Blackboard, or another Learning Management System?',
    detail: (
      <p className="mb-4">
        <a href="https://support.yellowdig.co/hc/en-us/articles/14092350664724">
          Follow these steps.
        </a>{' '}
        It is most likely that your browser is not storing cookies. Overzealous
        network firewalls and finicky browser extensions are other common causes
        of this launch failure.
      </p>
    )
  },
  {
    key: 'email-or-sign-in',
    term: 'Did you follow an email invite or sign-in to Yellowdig before you got here?',
    detail: (
      <>
        <p className="mb-4">
          Your browser is most likely not storing a cookie that we need to give
          you access to your community. Try solution C on{' '}
          <a href="https://support.yellowdig.co/hc/en-us/articles/14092350664724">
            this page.
          </a>{' '}
          If that doesn&apos;t work attempt the other solutions. You could also
          try to re-attempt getting{' '}
          <Link href="/user/login" exact>
            logged into your account
          </Link>
          .
        </p>
        <p className="yd-activity-feed__item__content__preview m-4">
          <span className="underline">Note:</span>
          {' '}
          Most students should never
          need to sign-in to Yellowdig. Typically you will be logged in through
          your learning management system or other student portal. If you did a
          web search for Yellowdig, tried to sign-in, and landed here, try
          looking for a link to Yellowdig inside of your learning management
          system instead.
        </p>
      </>
    )
  },
  {
    key: 'status',
    term: 'Could Yellowdig be temporarily down?',
    detail: (
      <p className="mb-4">
        Check our <a href="http://status.yellowdig.com">status page.</a>
      </p>
    )
  },
  {
    key: 'fallback',
    term: 'If all else fails...',
    detail: (
      <p className="mb-4">
        Search our comprehensive{' '}
        <a href="https://support.yellowdig.co">Knowledge Base.</a> If you still
        aren&apos;t able to stick the landing,{' '}
        <a href="https://support.yellowdig.co/hc/en-us/requests/new">
          contact support
        </a>{' '}
        with a detailed description of your problem and the steps taken to
        resolve it. Screenshots and GIFs are particularly helpful. The more
        information you give the quicker we&apos;ll have you Yellowdigging!
      </p>
    )
  }
]

const UserNotAuthorizedPage = () => {
  const { redirect } = useRouter().query

  const baseLoginUrl = '/user/login'
  const fullLoginUrl = redirect
    ? url.format({ pathname: baseLoginUrl, query: { redirect } })
    : baseLoginUrl

  // @TODO this should be handled with server actions
  // make sure cookies are removed
  useIsomorphicLayoutEffect(() => {
    removeSessionCookies()
  }, [redirect])

  return (
    <div className="mx-auto my-8 w-full rounded-lg bg-white p-2 py-8 sm:w-2/3">
      <h1 className="f3 text-center">You are not logged in.</h1>
      <p className="mx-auto mt-2 w-5/6 p-2 text-center">
        Click{' '}
        <Link href={fullLoginUrl} exact>
          here
        </Link>{' '}
        to login, or click on the tabs below for more troubleshooting tips.
      </p>
      <Accordion
        className="m-2 lg:m-8"
        items={accordionItems}
        sectionHeadingAriaLevel={H2}
      />
    </div>
  )
}

UserNotAuthorizedPage.getLayout = page => (
  <GlobalLayout title="Not Authorized">{page}</GlobalLayout>
)

UserNotAuthorizedPage.getInitialProps = ctx => {
  const { networkName } = ctx
  return {
    networkName
  }
}

export default UserNotAuthorizedPage
